import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  PlannedResourceRow,
  useUpdatePlannedResource,
} from 'components/IAP/hooks/assignmentListHooks';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import type { PlannedResourceDraft } from './assignmentCardFormTypes';
import { ICS204_HINT, ICS204_OUTLINE_BTN } from './assignmentCardStyles';
import { Ics204TextAreaField, Ics204TextField } from './Ics204Fields';
import Ics204LocationSelect from './Ics204LocationSelect';
import { ICS204A_SPECIAL_INSTRUCTIONS_HINT } from './ics204aBoxLabels';
import type { Ics204aEditingSection } from './ics204aSections';

export type Ics204aDisplayFields = {
  resourceBranch: string;
  resourceDivision: string;
  resourceGroup: string;
  resourceLeader: string;
  resourceAssignmentLocation: string;
  resourceSpecialInstructions: string;
  resourceSafetyPlanLocation: string;
};

type Ics204aSectionEditorProps = {
  plannedResource: PlannedResourceRow;
  display: Ics204aDisplayFields;
  onCommit?: (fields: Partial<PlannedResourceDraft>) => void;
  onLocalUpdate: (fields: Partial<Ics204aDisplayFields>) => void;
  onDone: () => void;
  onCancel: () => void;
};

const SectionActions = ({
  isSaving,
  onCancel,
}: {
  isSaving: boolean;
  onCancel: () => void;
}) => (
  <div className="tw:mt-3 tw:flex tw:items-center tw:gap-2">
    <Button type="submit" size="sm" disabled={isSaving}>
      {isSaving ? 'Saving…' : 'Save'}
    </Button>
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={ICS204_OUTLINE_BTN}
      disabled={isSaving}
      onClick={onCancel}
    >
      Cancel
    </Button>
  </div>
);

function usePersist204aSection({
  plannedResource,
  onCommit,
  onLocalUpdate,
  onDone,
}: Pick<
  Ics204aSectionEditorProps,
  'plannedResource' | 'onCommit' | 'onLocalUpdate' | 'onDone'
>) {
  const { mutateAsync: update } = useUpdatePlannedResource(plannedResource.id);
  const [isSaving, setIsSaving] = useState(false);

  const persist = async (
    displayPatch: Partial<Ics204aDisplayFields>,
    draftPatch: Partial<PlannedResourceDraft>
  ) => {
    setIsSaving(true);
    try {
      if (onCommit) {
        onCommit({
          has204a: true,
          ...draftPatch,
        });
      } else {
        await update({
          id: plannedResource.id,
          leader: draftPatch.leader ?? plannedResource.leader ?? '',
          numberOfPersons: plannedResource.numberOfPersons ?? 0,
          contactInfo: plannedResource.contactInfo ?? '',
          reportingInfo: plannedResource.reportingInfo ?? '',
          has204a: true,
          documentationName: plannedResource.documentationName,
          resourceBranch:
            draftPatch.resourceBranch ?? plannedResource.resourceBranch,
          resourceDivision:
            draftPatch.resourceDivision ?? plannedResource.resourceDivision,
          resourceGroup:
            draftPatch.resourceGroup ?? plannedResource.resourceGroup,
          resourceStrikeTeam: plannedResource.resourceStrikeTeam,
          resourceTaskForce: plannedResource.resourceTaskForce,
          resourceAssignmentLocation:
            draftPatch.resourceAssignmentLocation ??
            plannedResource.resourceAssignmentLocation,
          resourceSpecialInstructions:
            draftPatch.resourceSpecialInstructions ??
            plannedResource.resourceSpecialInstructions,
          resourceSafetyPlanLocation:
            draftPatch.resourceSafetyPlanLocation ??
            plannedResource.resourceSafetyPlanLocation,
        });
      }
      onLocalUpdate(displayPatch);
      onDone();
    } catch {
      // Mutation hooks toast; keep the form open for retry.
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, persist };
}

export const BranchSectionEdit = (props: Ics204aSectionEditorProps) => {
  const { display, onCancel } = props;
  const { data: positions } = useGetPositionsForIncident();
  const { isSaving, persist } = usePersist204aSection(props);
  const form = useForm({
    defaultValues: { resourceBranch: display.resourceBranch },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204a-section-edit-branch"
        onSubmit={form.handleSubmit(async (data) => {
          await persist(
            { resourceBranch: data.resourceBranch },
            { resourceBranch: data.resourceBranch }
          );
        })}
      >
        <Ics204LocationSelect
          label="Branch"
          fieldName="resourceBranch"
          testId="ics204a-edit-branch"
          category="Branch"
          positions={positions ?? []}
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const DivisionGroupSectionEdit = (props: Ics204aSectionEditorProps) => {
  const { display, onCancel } = props;
  const { data: positions } = useGetPositionsForIncident();
  const { isSaving, persist } = usePersist204aSection(props);
  const form = useForm({
    defaultValues: {
      resourceDivision: display.resourceDivision,
      resourceGroup: display.resourceGroup,
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204a-section-edit-divisionGroup"
        onSubmit={form.handleSubmit(async (data) => {
          await persist(
            {
              resourceDivision: data.resourceDivision,
              resourceGroup: data.resourceGroup,
            },
            {
              resourceDivision: data.resourceDivision,
              resourceGroup: data.resourceGroup,
            }
          );
        })}
      >
        <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
          <Ics204LocationSelect
            label="Division"
            fieldName="resourceDivision"
            testId="ics204a-edit-division"
            category="Division"
            positions={positions ?? []}
          />
          <Ics204LocationSelect
            label="Group"
            fieldName="resourceGroup"
            testId="ics204a-edit-group"
            category="Group"
            positions={positions ?? []}
          />
        </div>
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const LeaderSectionEdit = (props: Ics204aSectionEditorProps) => {
  const { display, onCancel } = props;
  const { isSaving, persist } = usePersist204aSection(props);
  const form = useForm({
    defaultValues: { resourceLeader: display.resourceLeader },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204a-section-edit-leader"
        onSubmit={form.handleSubmit(async (data) => {
          await persist(
            { resourceLeader: data.resourceLeader },
            { leader: data.resourceLeader }
          );
        })}
      >
        <Ics204TextField
          fieldName="resourceLeader"
          testId="ics204a-edit-leader"
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const AssignmentLocationSectionEdit = (
  props: Ics204aSectionEditorProps
) => {
  const { display, onCancel } = props;
  const { isSaving, persist } = usePersist204aSection(props);
  const form = useForm({
    defaultValues: {
      resourceAssignmentLocation: display.resourceAssignmentLocation,
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204a-section-edit-assignmentLocation"
        onSubmit={form.handleSubmit(async (data) => {
          await persist(
            {
              resourceAssignmentLocation: data.resourceAssignmentLocation,
            },
            {
              resourceAssignmentLocation: data.resourceAssignmentLocation,
            }
          );
        })}
      >
        <Ics204TextField
          fieldName="resourceAssignmentLocation"
          testId="ics204a-edit-assignment-location"
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const SpecialInstructionsSectionEdit = (
  props: Ics204aSectionEditorProps
) => {
  const { display, onCancel } = props;
  const { isSaving, persist } = usePersist204aSection(props);
  const form = useForm({
    defaultValues: {
      resourceSpecialInstructions: display.resourceSpecialInstructions,
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204a-section-edit-specialInstructions"
        onSubmit={form.handleSubmit(async (data) => {
          await persist(
            {
              resourceSpecialInstructions: data.resourceSpecialInstructions,
            },
            {
              resourceSpecialInstructions: data.resourceSpecialInstructions,
            }
          );
        })}
      >
        <p className={ICS204_HINT}>{ICS204A_SPECIAL_INSTRUCTIONS_HINT}</p>
        <Ics204TextAreaField
          fieldName="resourceSpecialInstructions"
          testId="ics204a-edit-special-instructions"
          className="tw:mt-0"
          rules={{
            maxLength: {
              value: 255,
              message: 'This maximum length is 255 characters',
            },
          }}
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const SiteSafetyPlanSectionEdit = (props: Ics204aSectionEditorProps) => {
  const { display, onCancel } = props;
  const { isSaving, persist } = usePersist204aSection(props);
  const form = useForm({
    defaultValues: {
      resourceSafetyPlanLocation: display.resourceSafetyPlanLocation,
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204a-section-edit-siteSafetyPlan"
        onSubmit={form.handleSubmit(async (data) => {
          await persist(
            {
              resourceSafetyPlanLocation: data.resourceSafetyPlanLocation,
            },
            {
              resourceSafetyPlanLocation: data.resourceSafetyPlanLocation,
            }
          );
        })}
      >
        <Ics204TextAreaField
          fieldName="resourceSafetyPlanLocation"
          testId="ics204a-edit-safety-plan-location"
          className="tw:mt-0"
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const renderIcs204aSectionEditor = (
  section: Ics204aEditingSection,
  props: Ics204aSectionEditorProps
) => {
  switch (section) {
    case 'branch':
      return <BranchSectionEdit {...props} />;
    case 'divisionGroup':
      return <DivisionGroupSectionEdit {...props} />;
    case 'leader':
      return <LeaderSectionEdit {...props} />;
    case 'assignmentLocation':
      return <AssignmentLocationSectionEdit {...props} />;
    case 'specialInstructions':
      return <SpecialInstructionsSectionEdit {...props} />;
    case 'siteSafetyPlan':
      return <SiteSafetyPlanSectionEdit {...props} />;
    default:
      return null;
  }
};
